<template>
	<div class="unlock-history">
		<div class="unlock-history__title">
			{{ translate("ui.unlock_history") }}
		</div>
		<HorizontalDecorDivider />
		<div class="paginator">
			<AppIconButton
				:type="IconTypes.ARROW_LEFT"
				:size="9"
				@click="changeLevel(-1)"
				:class="['arrow-button', { disabled: currentViewLevel === 1 }]"
			/>
			<span class="paginator__text">{{
				`${translate("ui.level")} ${currentViewLevel}`
			}}</span>
			<AppIconButton
				:type="IconTypes.ARROW_RIGHT"
				:size="9"
				@click="changeLevel(1)"
				:class="[
					'arrow-button',
					{ disabled: currentViewLevel === mainLevel },
				]"
			/>
		</div>
		<section class="content" v-if="noNewFeatures">
			<div class="content__text">
				{{ translate("ui.no_new_features") }}
			</div>
		</section>
		<section class="content" v-else>
			<ScrollFade>
				<template #scrollContent>
					<div
						class="content__box"
						v-if="computedBuildings.length > 0"
					>
						<div class="subsection-header">
							<RaggedBoxBackground
								slug="subsection-bg"
								:skin="{
									stopColor1: '#d7bd8d',
									stopColor2: '#d7bd8d',
								}"
							/>
							<div class="subsection-text">
								<span class="subsection-text__text">
									{{ translate("ui.unlocked_buildings") }}
								</span>
								<AppNotification
									:value="computedBuildings.length"
								/>
							</div>
						</div>
						<TileUpgradeBox
							v-for="(entity, index) in computedBuildings"
							:key="index"
							:heading="entity.name"
							:description="entity.description"
							:type="TileUpgradeBoxTypes.LIST"
							:consumption="entity?.consumption ?? null"
							:entity="entity"
							:icon-name="getTileIconName(entity.frameName)"
							:context="UIContexts.UNLOCK_HISTORY"
							:skin="
								tilerepoPremiumBuildings.has(entity.frameName)
									? TileUpgradeBoxSkinTypes.BUILDING_PREMIUM
									: TileUpgradeBoxSkinTypes.BUILDING
							"
							:isPromotion="true"
						/>
					</div>
					<div
						v-if="computedUpgrades.length > 0"
						class="content__box"
					>
						<div class="subsection-header">
							<RaggedBoxBackground
								slug="subsection-bg"
								:skin="{
									stopColor1: '#d7bd8d',
									stopColor2: '#d7bd8d',
								}"
							/>
							<div class="subsection-text">
								<span class="subsection-text__text">
									{{ translate("ui.unlocked_upgrades") }}
								</span>
								<AppNotification
									:value="computedUpgrades.length"
								/>
							</div>
						</div>
						<UpgradeBox
							v-for="(upgrade, index) in computedUpgrades"
							:key="index"
							:has-redirect="false"
							:skin="SkinTypes.LIGHT"
							:id="upgrade[0]"
							:heading="upgrade[1]?.heading"
							:description="upgrade[1]?.description"
							:descriptionReplace="upgrade[1]?.descriptionReplace"
						/>
					</div>
				</template>
			</ScrollFade>
		</section>
	</div>
</template>

<script lang="ts" setup>
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import { translate } from "@/utils/translate";

import AppIconButton from "@/components/AppIconButton.vue";
import AppNotification from "@/components/AppNotification.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import TileUpgradeBox from "@/components/TileUpgradeBox.vue";
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import { SAFE_COORDINATE_OUTSIDE_MAP } from "@/config/constants";
import {
	type FrameName,
	tilerepoDuplicateFrameNames,
	tilerepoPremiumBuildings,
} from "@/game/entities/config";
import { getNewBuildingsFromLevel } from "@/game/entities/config/buildings";
import { getNewRoadsFromLevel } from "@/game/entities/config/roads";
import { getNewTerrainsFromLevel } from "@/game/entities/config/terrain";
import {
	type UpgradeId,
	type UpgradeItem,
	getNewUpgradesFromLevel,
} from "@/game/entities/config/upgrades";
import type TileEntity from "@/game/entities/TileEntity";
import useTileRepository from "@/hooks/useTileRepository";
import useSettlementsStore from "@/store/useSettlementsStore";
import {
	IconTypes,
	SkinTypes,
	TileUpgradeBoxSkinTypes,
	TileUpgradeBoxTypes,
	UIContexts,
} from "@/types/ui";
import { getTileIconName } from "@/utils/tileIconName";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";

const settlementStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementStore);
const tilerepo = markRaw(useTileRepository());
const currentViewLevel = ref<number>(mainLevel.value);

const computedBuildings = computed((): Array<TileEntity> => {
	const buildings = getNewBuildingsFromLevel(currentViewLevel.value);
	const terrains = getNewTerrainsFromLevel(currentViewLevel.value);
	const roads = getNewRoadsFromLevel(currentViewLevel.value);
	const all = [...buildings, ...terrains, ...roads];
	return all
		.filter(
			(
				frameName: FrameName,
				index: number,
				self: Array<FrameName>,
			): boolean => self.indexOf(frameName) === index,
		)
		.map((frameName: FrameName): TileEntity | null => {
			const entity = tilerepo.createEntityByFrameName(frameName, {
				x: SAFE_COORDINATE_OUTSIDE_MAP,
				y: SAFE_COORDINATE_OUTSIDE_MAP,
			});
			if (!entity) return null;

			entity.showProductionInfo = true;
			entity.showConsumptionInfo = true;
			return entity;
		})
		.filter((entity: TileEntity | null): entity is TileEntity => {
			return (
				entity !== null &&
				// toto zabranuje duplikatom vo vypise novych budov
				!tilerepoDuplicateFrameNames.has(entity.frameName) &&
				!entity.frameName.toLowerCase().includes("tutorial")
			);
		})
		.sort(
			(
				{ frameName: aFrameName }: TileEntity,
				{ frameName: bFrameName }: TileEntity,
			): number =>
				buildings.indexOf(aFrameName) - buildings.indexOf(bFrameName),
		);
});

const computedUpgrades = computed((): [UpgradeId, UpgradeItem][] => {
	return Array.from(
		getNewUpgradesFromLevel(currentViewLevel.value).entries(),
	) as [UpgradeId, UpgradeItem][];
});

const changeLevel = (delta: number): void => {
	const newLevel = currentViewLevel.value + delta;
	if (newLevel < 1 || newLevel > mainLevel.value) return;
	currentViewLevel.value = newLevel;
};

const noNewFeatures = computed((): boolean => {
	return (
		computedBuildings.value.length === 0 &&
		computedUpgrades.value.length === 0
	);
});
</script>

<style lang="scss" scoped>
.unlock-history {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 0.5rem;
	padding: 1.25rem;
	padding-top: 0;
	&__title {
		padding-top: 0.5rem;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1;
		text-align: center;
		text-transform: uppercase;
	}
	&__item {
		&-header {
			display: flex;
			justify-content: space-between;
			color: #f4d278;
			& > div {
				font-weight: bold;
				font-size: 1rem;
			}
		}
		&-title {
			width: 80%;
		}
		&-text {
			color: #fff3dd;
		}
	}
	:deep() {
		.horizontal-decor-divider {
			width: 100%;
		}
		ul {
			margin-left: 1rem;
		}
		a {
			cursor: pointer;
			color: #f2dcb5;
			text-decoration: underline;
		}
	}

	.paginator {
		@include flex-row-center;
		gap: 0.5rem;

		&__text {
			padding-bottom: 0.125rem;
			color: #f4d278;
			font-size: 1rem;
		}

		.arrow-button {
			padding: 0 0.375rem;
			width: 1.25rem;
			height: 1.25rem;

			&.disabled {
				opacity: 0.5;
				pointer-events: none;
			}
		}
	}
	.content {
		@include flex-col-center;
		gap: 0.5rem;
		box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.1);
		border-radius: 0.15rem;
		background: url($path-images + "backgrounds/textures/window-texture-bg.avif")
			repeat center;
		background-size: 90% auto;
		padding: 0.125rem;
		width: 25rem;

		&::before {
			position: absolute;
			top: 0.125rem;
			left: 0.125rem;
			z-index: -1;
			border-radius: 0.15rem 0.15rem 0 0;
			width: calc(100% - 0.25rem);
			height: 100%;
			pointer-events: none;
			content: "";
		}

		:deep() {
			.scrollbar__wrapper {
				.scrollbar__content {
					gap: 0.5rem;
					padding: 0.625rem;
					@include flex-col-start;
				}
			}
		}

		&__box {
			width: 100%;
			@include flex-col-center;
			gap: 0.5rem;

			:deep() {
				.upgrade-box__description-effect {
					color: #13cedf;
				}
			}
		}

		.subsection-header {
			position: relative;
			width: 100%;
			height: 2.5rem;
			:deep() {
				.ragged-box-bg {
					position: absolute;
					box-shadow: 0 0 0.75rem 0 rgba(49, 37, 22, 0.6);
					width: 100%;
					height: 100%;
				}
			}

			.subsection-text {
				position: relative;
				padding: 0 0.75rem;
				width: 100%;
				height: 100%;
				@include flex-row-between;

				&__text {
					padding-bottom: 0.25rem;
					color: rgb(55, 38, 21);
					font-weight: 800;
					font-size: 1.0625rem;
					text-transform: uppercase;
				}
			}
		}

		&__text {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 100%;
			min-height: 15rem;
			color: #f4d278;
			font-size: 1rem;
		}
	}
}
</style>
